import {Component, OnInit} from "@angular/core";
import {Title} from "@angular/platform-browser";
import {ActivatedRoute} from "@angular/router";
import * as _ from "lodash";
import {ToastrService} from "ngx-toastr";
import {BehaviorSubject, of, Subscription} from "rxjs";
import {debounceTime, tap} from "rxjs/operators";
import keys from "../../../../assets/i18n/translation-keys.json";
import {Language} from "../../../models/language";
import {LanguageControllerService} from "../../../services/language-controller/language-controller.service";
import {ServiceControllerService} from "../../../services/service-controller/service-controller.service";
import {TranslateService} from "@ngx-translate/core";
import {TranslatedBaseError} from "../TranslatedBaseError";
import {Location} from "@angular/common";
import {PreviewTranslationsControllerService} from "../../../services/preview-translations-controller/preview-translations-controller.service";
import {GeneralTranslation} from "../../../models/general-translation";
import {AuthService} from "../../../services/auth/auth-service";
import {environment} from "../../../../environments/environment";
import {Store} from "../../../services/store.service";

@Component({
  selector: "translations",
  templateUrl: "./translations.component.html",
  providers: [PreviewTranslationsControllerService],
})
export class TranslationsComponent extends TranslatedBaseError implements OnInit {
  public isTableBodyVisible = false;
  public languages: Language[];
  public services: any[] = [];
  public serviceForms: any[] = [];
  public serviceId: string = "general";
  public formId: string = "all";
  public myTranslations: any;
  public translationsPerPage = 25;
  public currentLanguage: string;
  public keys: string[];
  public isGlobalReady: boolean = false;
  public areServicesReady: boolean = false;
  public searchTrad: string;
  public exactMatch: boolean = false;
  public searchTranslation$ = new BehaviorSubject("");
  public searchSubscription?: Subscription;
  public searchCounter = 0;

  public isTranslationManager: boolean = false;
  public isGlobalTranslator: boolean = false;
  public isLocalTranslator: boolean = false;

  public loadingGlobal: boolean = false;
  public loadingCache: boolean = false;
  public authVisibility: boolean = false;

  public bpaProperties: any = { localAuthorized: false, localSupersedesGlobal: false };
  public thirdState: any = { localAuthorized: false, localSupersedesGlobal: false };
  public filters: string = "BPA,DS,STATISTICS";
  public selectedFilters: string = "all";
  public selectedService: string = "general";
  public selectedForm: string = "all";

  public page: number = 1;
  public pageSize: number = 2;
  public allItems: number = 1000;

  public selectedTranslation: any;
  public selectedTranslationIndex: number = -1;
  public confirmDeletation: boolean = false;

  constructor(private languageService: LanguageControllerService,
              private serviceControllerService: ServiceControllerService,
              private route: ActivatedRoute,
              private location: Location,
              private title: Title,
              public translateService: TranslateService,
              private previewTranslationController: PreviewTranslationsControllerService,
              private authService: AuthService,
              public store: Store,
              toastr: ToastrService) {
    super(toastr, translateService);
    // @ts-ignore
    this.keys = (Array.from(keys)).sort();
    this.route.params.subscribe((params) => {
      if (!params || !params.lang) {
        this.currentLanguage = environment.DEFAULT_LANGUAGE;
      } else {
        this.currentLanguage = params.lang;
      }
      if (!params || !params.serviceId) {
        this.serviceId = "general";
      } else {
        this.serviceId = params.serviceId;
      }
    });
  }

  public async ngOnInit() {
    this.title.setTitle("Translation");
    this.loadServices();
    this.loadLanguages();
    this.loadTranslations(this.currentLanguage, this.serviceId, this.page);
    this.searchSubscription = this.searchTranslation$.pipe(
      debounceTime(300),
    ).pipe(
      tap((search) => {
          this.searchTrad = search;
          this.searchTranslations();
        },
      )).subscribe();
    await this.determineTranslationPermission();
  }

  public ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  public loadLanguages() {
    this.languageService.getLanguages().subscribe(
      (data) => {
        this.languages = data;
      },
      (error) => this.showErrorMessageWithTitle("Error with language loading:" + " ", error),
    );
  }

  public loadServices() {
    this.areServicesReady = false;
    this.serviceControllerService.getServicesList().subscribe(
      (data: any) => this.buildServices(data),
      (error) => this.showErrorMessageWithTitle("Error with service list loading:" + " ", error),
    );
  }

  public buildServices(services: any[]) {
    for (const service of services) {
      if (!service.active) {
        continue;
      }
      const s = {
        id: service.id,
        name: service.name.toLowerCase(),
      };
      this.services.push(s);
    }
    this.services = _.orderBy(this.services, ["name"]);
    this.areServicesReady = true;
  }

  public switchServices(id) {
    if (this.serviceId !== id) {
      this.serviceId = id;
      this.selectedService = id;
      this.page = 1;
      this.formId = "all";
      this.selectedForm = "all";
      this.loadServiceForms(this.serviceId);
      this.loadTranslations(this.currentLanguage, this.serviceId, this.page);
    }
  }

  public switchForms(id) {
    if (this.formId !== id) {
      this.formId = id;
      this.selectedForm = id;
      this.page = 1;
      this.loadTranslations(this.currentLanguage, this.serviceId, this.page);
    }
  }

  private onLoadError(error: any) {
    this.showErrorMessageWithTitle("Error with translations loading:" + " ", error);
  }

  public loadServiceForms(serviceId: string = "general") {
    if (serviceId === "general") {
      this.serviceForms = [];
      this.formId = "all";
      return;
    }

    this.serviceControllerService.getServiceForms(serviceId).subscribe((data: any[]) => {
      this.serviceForms = [{id: "all", name: "all"}].concat(data.map(item => {
        item.id = item.businessKey;
        return item;
      }));
    });
  }

  public updateExactMatch(): void {
    this.setExactMatch(!this.exactMatch);
  }

  public setExactMatch(value: boolean): void {
    this.exactMatch = value;
    this.loadTranslations(this.currentLanguage, this.selectedService, 1);
  }

  public loadTranslations(language: string = environment.DEFAULT_LANGUAGE, serviceId: string = "general", page: number = 1, cb = null) {
    this.isTableBodyVisible = false;

    if (this.searchTrad && this.searchTrad.length > 0) {
      this.searchTranslations(page, this.selectedService, cb);
    } else {
      this.myTranslations = [];
      if (!this.searchTrad || !(this.selectedFilters === "service" && serviceId === "general")) {
        this.page = page;

        const form = this.selectedForm !== "all" ? this.serviceForms.find(sf => sf.id === this.selectedForm) : false;

        this.previewTranslationController.getTranslationsPage(serviceId, page, this.translationsPerPage, this.filters, form).subscribe(
          (data) => {
            this.myTranslations = data;
            this.isGlobalReady = true;
            this.isTableBodyVisible = true;
            TranslationsComponent.updateTextareasHeight();
            this.allItems = this.myTranslations.totalResults ? this.myTranslations.totalResults : 0;
            if (cb) { cb(); }
          },
          (error) => this.showErrorMessage(error),
        );
      }
    }
  }

  public static updateTextareasHeight() {
    setTimeout(() => {
      const areas = document.querySelectorAll(".input-local") as any as HTMLElement[];
      areas.forEach((element) => {
        element.style.height = "auto";
        element.style.height = element.scrollHeight + "px";
      });
    }, 1000);
  }

  public fromTypeToClass(type) {
    if (type) {
      if (this.selectedFilters === "service") {
        return `translation-${this.selectedFilters}`;
      }
      return `translation-${type.toLowerCase()}`;
    } else {
      return;
    }
  }

  public saveTranslation(translation: GeneralTranslation) {
    this.previewTranslationController.saveTranslation(this.serviceId, translation).subscribe(
      (data) => {
        translation.id = data.id;
        this.responseSuccessful("Translations saved!");
      },
    );
  }

  public pageNumber() {
    const pages = [];
    let index = 1;
    for (let i = 0; i <= this.allItems - 1; i += 25) {
      pages.push(index);
      index++;
    }
    return pages;
  }

  public saveSetting(name: string, value: boolean, translation: any) {
    if (translation) {
      for (const lang of this.languages) {
        if (translation[lang.code]) {
          translation[lang.code][name] = value;
        }
      }
    }
    this.previewTranslationController.saveTranslations(this.serviceId, translation).subscribe(
      (data) => {
        if (Array.isArray(data)) {
          data.filter((item) => !translation[item.languageCode].id).forEach((item) => {
            translation[item.languageCode].id = item.id;
          });
        }
        this.responseSuccessful("Translations saved!");
        this.getTranslationSettingsStates();
      },
    );
  }

  public onGlobalTranslationsChange(translation: GeneralTranslation) {
    if (!translation) {
      return;
    }
    return this.previewTranslationController.saveGlobalTranslation(translation).subscribe(
      (data) => {
        translation.id = data.id;
        this.responseSuccessful("Translations saved!");
      },
      (error) => this.onLoadError(error),
    );
  }

  public supersedeInfos(t) {
    let tp = "N/A";
    if (t.localSupersedesGlobalLastModifiedBy) {
      tp = t.localSupersedesGlobalLastModifiedBy.split("(")[0];
    }
    if (t.localSupersedesGlobalLastModifiedBy && t.localSupersedesGlobalLastModifiedDate) {
      tp += "\n";
    }
    if (t.localSupersedesGlobalLastModifiedDate) {
      const date = new Date(t.localSupersedesGlobalLastModifiedDate);
      tp += date.getDate() + "-" + date.getMonth() + "-" + date.getFullYear();
    }
    return tp;
  }

  public localesInfos(t) {
    let tp = "";
    if (t.localLastModifiedBy) {
      tp = t.localLastModifiedBy.split("(")[0];
    }
    if (t.localLastModifiedBy && t.localLastModifiedDate) {
      tp += "\n";
    }
    if (t.localLastModifiedDate) {
      const date = new Date(t.localLastModifiedDate);
      tp += date.getDate() + "-" + date.getMonth() + "-" + date.getFullYear();
    }
    return tp;
  }

  private static feMatrixBuilder(lang, key) {
    const object = {};
    object[lang] = {
      fieldId: key,
      fieldName: key,
      languageCode: lang,
      type: "BPA",
      globalName: undefined,
    };
    return object;
  }

  private addMissingFELabels(slots: number, exactMatch: boolean) {
    if (this.serviceId !== "general") { return; }
    const lookupText = this.searchTrad.toLowerCase();
    const missingKeys = this.keys.filter((key) => {
      if (slots === 0) { return false; }
      let match = exactMatch
        ? key.toLowerCase() === lookupText
        : key.toLowerCase().includes(lookupText);
      if (!match) { return false; }
      match = this.myTranslations.items.every((item) => {
        const translation = item[this.languages[0].code];
        return !!translation.serviceId || translation.type !== "BPA" || translation.fieldId !== key;
      });
      if (match) { slots--; }
      return match;
    });

    missingKeys.forEach((key) => {
      const newItem = {};
      this.languages.forEach((lnT) => {
        Object.assign(newItem, TranslationsComponent.feMatrixBuilder(lnT.code, key));
      });
      this.myTranslations.items.push(newItem);
    });
  }

  public searchTranslations(page: number = 1, serviceId: string = "general", cb = null) {
    if (this.searchTrad && this.searchTrad.length > 0) {
      if (this.searchTrad === " ") {
        this.searchTrad = "";
      }
      this.page = page;
      this.searchCounter++;
      const numberOfSearch = this.searchCounter;
      this.myTranslations = [];
      const form = this.selectedForm !== "all" ? this.serviceForms.find(sf => sf.id === this.selectedForm) : false;
      this.previewTranslationController.searchTranslations(serviceId, this.searchTrad, this.filters, page, this.translationsPerPage, this.exactMatch, form).subscribe(
        (data) => {
          if (numberOfSearch !== this.searchCounter) { return; } // another search already triggered
          this.myTranslations = data;
          // @ts-ignore
          if ((data.totalResults < (data.resultsPerPage * data.pageNumber)) && this.filters.includes('BPA'))  {
            // @ts-ignore
            this.addMissingFELabels((data.resultsPerPage * data.pageNumber) - data.totalResults, this.exactMatch);
          }
          this.isGlobalReady = true;
          this.isTableBodyVisible = true;
          TranslationsComponent.updateTextareasHeight();
          this.allItems = this.myTranslations.totalResults ? this.myTranslations.totalResults : 0;
          if (cb) { cb(); }
        },
        (error) => this.showErrorMessage(error),
      );
    } else if (this.searchTrad || this.searchTrad === "" || this.searchTrad === null) {
      this.loadTranslations();
    }
  }

  private async determineTranslationPermission() {
    (await this.authService.getUserRoles()).forEach((role) => {
      // noinspection FallThroughInSwitchStatementJS
      switch (role.toLowerCase()) {
        case AuthService.translationManager.toLowerCase():
          this.isTranslationManager = true;
        case AuthService.globalTranslator.toLowerCase():
          this.isGlobalTranslator = true;
        case AuthService.localTranslator.toLowerCase():
          this.isLocalTranslator = true;
      }
    });
    if (this.isTranslationManager) { this.getTranslationSettingsStates(); }
  }

  public reloadTranslationsCache(): void {
    this.loadingCache = true;
    this.previewTranslationController.reloadTranslationsCache().subscribe(
      (response) => {
        // @ts-ignore
        this.responseSuccessful(response && response.message);
        this.loadingCache = false;
      },
      (error) => {
        this.showErrorMessage(error);
        this.loadingCache = false;
      },
    );
  }

  public reloadGlobalTranslations(): void {
    this.loadingGlobal = true;
    this.previewTranslationController.reloadGlobalTranslations().subscribe(
      (response) => {
        try {
          const resp = JSON.parse(response);
          const reloadSettings = () => {
            this.getTranslationSettingsStates();
          };
          this.loadTranslations(this.currentLanguage, this.serviceId, this.page, reloadSettings);
          this.responseSuccessful(resp && resp.message);
        } catch (e) {
          this.showErrorMessage(e);
        }
        this.loadingGlobal = false;
      },
      (error) => {
        this.showErrorMessage(error);
        this.loadingGlobal = false;
      },
    );
  }

  public updateSystemProps(prop: string) {
    this.bpaProperties[prop] = !this.bpaProperties[prop];
    const updateObj = { bpaProperties: {}};
    updateObj.bpaProperties[prop] = this.bpaProperties[prop];

    const cb = (data) => {
      this.loadTranslations(this.currentLanguage, this.serviceId, this.page);
      this.thirdState[prop] = false;
      // @ts-ignore
      this.responseSuccessful(data && data.message);
    };
    return this.updateSystemProperties(updateObj, cb);
  }

  private updateSystemProperties(properties, cb) {
    this.previewTranslationController.updateSystemProperties(properties).subscribe(
      (data) => {
        return cb && cb(data);
      },
      (error) => this.showErrorMessage(error),
    );
  }

  public getTranslationSettingsStates() {
    this.previewTranslationController.getTranslationSettingsStates().subscribe((data) => {
      // @ts-ignore
      const localAuthorizedFalsePresent = data.localAuthorizedFalse;
      // @ts-ignore
      const localAuthorizedTruePresent = data.localAuthorizedTrue;
      // @ts-ignore
      const localSupersedesGlobalFalsePresent = data.localSupersedesGlobalFalse;
      // @ts-ignore
      const localSupersedesGlobalTruePresent = data.localSupersedesGlobalTrue;

      this.thirdState.localAuthorized = localAuthorizedFalsePresent && localAuthorizedTruePresent;
      this.thirdState.localSupersedesGlobal = localSupersedesGlobalFalsePresent && localSupersedesGlobalTruePresent;

      if (!localAuthorizedFalsePresent && !this.bpaProperties.localAuthorized) {
        this.bpaProperties.localAuthorized = true;
      }

      if (!localSupersedesGlobalFalsePresent && !this.bpaProperties.localSupersedesGlobal) {
        this.bpaProperties.localSupersedesGlobal = true;
      }
    }, (error) => this.showErrorMessage(error));

  }

  public displayFieldName(translation) {
    switch (translation.location) {
      case "SERVICE_NAME":
      case "SERVICE_DESCRIPTION":
      case "SERVICE_REGISTRATION":
      case "SERVICE_REGISTRATION_SHORTNAME":
      case "SERVICE_REGISTRATION_REQUIREMENT":
      case "SERVICE_REGISTRATION_COST":
      case "SERVICE_ROLE":
      case "SERVICE_ROLE_SHORTNAME":
      case "SERVICE_TEMPLATE":
      case "FORM_FIELD":
      case "FORM_BLOCK":
      case "GUIDE_TAB":
        return true;
      default:
        return false;
    }
  }

  public setTranslationToDelete(translation, i) {
    this.confirmDeletation = true;
    this.selectedTranslation = translation;
    this.selectedTranslationIndex = i;
    this.selectedTranslation.name = this.displayFieldName(translation[this.languages[0].code]) ? translation[this.languages[0].code].fieldName : translation[this.languages[0].code].fieldId;
  }

  public cancelDeletion() {
    this.confirmDeletation = false;
    this.selectedTranslation = null;
    this.selectedTranslationIndex = -1;
  }

  public displayGlobalFields(translation) {
    if (translation.serviceId) { return false; }
    return !this.displayFieldName(translation);
  }

  public displayDeleteBtn(translation) {
    if (Object.keys(translation).map((key) => translation[key] && translation[key].id).filter((id) => !!id).length === 0) { return false; }
    if (translation[this.languages[0].code].serviceId) { return true; }
    if (this.displayFieldName(translation[this.languages[0].code])) { return true; }
    return this.isTranslationManager;
  }

  public displayGlobalDeleteBtn(translation) {
    if (Object.keys(translation).map((key) => translation[key] && translation[key].id).filter((id) => !!id).length === 0) { return false; }
    if (translation[this.languages[0].code].serviceId) { return false; }
    if (this.displayFieldName(translation[this.languages[0].code])) { return false; }
    return this.isTranslationManager;
  }

  public deleteTranslation(translation, index) {
    const ids = Object.keys(translation).map((key) => translation[key] && translation[key].id).filter((id) => !!id);
    this.previewTranslationController.deleteTranslationsByIds(ids).subscribe(
      (data) => {
        // @ts-ignore
        if (data.unauthorizedIds && data.unauthorizedIds.length > 0) {
          this.error("Only TRANSLATION_MANAGER is allowed to delete this translation",
            "Translation deletion unauthorized");
          return;
        }
        this.myTranslations.items.splice(index, 1);
        this.getTranslationSettingsStates();
        this.responseSuccessful("Translation deleted!");
        this.cancelDeletion();
      },
    );
  }

  public deleteGlobalTranslation(translation, index) {
    const id = Object.keys(translation).map((key) => translation[key] && translation[key].id).filter((item) => !!item)[0];
    this.previewTranslationController.deleteGlobalTranslationsById(id).subscribe(
      (data) => {
        this.myTranslations.items.splice(index, 1);
        this.getTranslationSettingsStates();
        this.responseSuccessful("Translation deleted!");
        this.cancelDeletion();
      },
    );
  }

}
